import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AppShell, { WhiteBox } from 'components/AppShell';
import { useServices } from 'contexts';
import { getShortlistFeedback } from 'services/shortlistingService';
import { Box, Divider, LinearProgress, Link, Skeleton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Session } from 'utils';
import CircleProgressbarWithInfo from 'components/CircleProgressbar';
import { useTheme } from '@emotion/react';
import { getCommentAndColor } from 'components/PerformanceReview/helpers';
import { CopyButton } from 'components/CustomButton';
import { InfoOutlined } from '@mui/icons-material';
import RateExperienceDialog from 'dialogs/RateExperienceDialog';

const useStyles = makeStyles((theme) => ({
  container : {
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(4),
    width : '100%'
  },
  scoreBox : {
    width : '462px',
    height : '180px',
    maxWidth : '462px',
    border : `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius : theme.spacing(2),
    display : 'flex',
    justifyContent : 'center',
    alignItems : 'center',
    padding : theme.spacing(5)
  },
  dividerBox : {
    height : '75px',
    width : '18px',
    borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
    borderBottom: `1px solid ${theme.palette.neutral['clr-200']}`,
    marginBottom : theme.spacing(6)
  },
  summaryBox : {
    width : '100%',
    height : '180px',
    maxHeight : '180px',
    border : `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius : theme.spacing(2),
    padding : theme.spacing(7),
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(3)
  },
  strengthBox : {
    width : '100%',
    border : `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius : theme.spacing(2),
    padding : theme.spacing(7),
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(3),
    position : 'relative'
    // backgroundColor : '#DCFCE74D'
  },
  suggestionBox : {
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(1),
    width : '100%',
    border : `1px solid #1961BF`,
    backgroundColor : '#F5F9FF66',
    borderRadius : theme.spacing(2),
    padding : theme.spacing(2,3,2,3),
  },
  bottomContent : {
    height : '48px',
    width : '100%',
    display : 'flex',
    // justifyContent : 'center',
    paddingLeft : theme.spacing(5),
    alignItems : 'center',
    border : `1px solid ${theme.palette.neutral['clr-200']}`,
    backgroundColor : '#F8F9FA',
    borderRadius : theme.spacing(0,0,2,2),
    position : 'absolute',
    bottom : 0,
    left : 0
  }
}));

const ShortlistFeedback = () => {
  const { id } = useParams(); 
  const service = useServices();
  const classes = useStyles();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [feedback , setFeedback] = useState()
  const [expandedIndexes, setExpandedIndexes] = useState([]);
  const [openFeedback , setOpenFeedback] = useState(false);

  const handleToggle = (index) => {
    setExpandedIndexes((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        const fetchFeedback = await service.callService(getShortlistFeedback, id);
        setFeedback(fetchFeedback)
      } catch (error) {
        console.error('Error fetching feedback:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFeedback();
    }
  }, []);

  useEffect(() => {
    const shortlistFeedbackCount = parseInt(localStorage.getItem('resumeAnalyseCount')) || 0;
    
    if (shortlistFeedbackCount > 0 && shortlistFeedbackCount % 3 === 0) {
      setOpenFeedback(true);
    }
  }, []);

  const skillColor = getCommentAndColor(feedback?.analysis.skillMatchScore)
  const experienceColor = getCommentAndColor(feedback?.analysis.relevantExperienceScore)
  const scoreColor = getCommentAndColor(feedback?.score)

  return (
    <AppShell headerLabel="Shortlisting Genie">
      <WhiteBox>
        {loading ? (
          <Box className={classes.container}>
          <Box display='flex' flexDirection='column' gap='8px'>
            <Skeleton variant="text" width={240} height={32} />
            <Skeleton variant="text" width={480} height={28} />
          </Box>
          <Box display='flex' gap='16px' marginTop='12px'>
            <Box className={classes.scoreBox}>
              <Skeleton variant="circular" width={144} height={144} />
              <Divider sx={{width : '49px' , borderColor : '#CCD4DE'}}/>
              <Divider orientation='vertical' sx={{height : '75px' , borderColor : '#CCD4DE' , marginBottom : '24px'}}/>
              <Box className={classes.dividerBox}></Box>
              <Box display='flex' flexDirection='column' gap='24px'>
                <Skeleton variant="rectangular" width={216} height={24} />
                <Skeleton variant="rectangular" width={216} height={24} />
              </Box>
            </Box>
            <Box className={classes.summaryBox}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" width="100%" height={84} />
            </Box>
          </Box>
          <Box className={classes.strengthBox} sx={{backgroundColor : '#DCFCE74D'}}>
            <Skeleton variant="text" width={200} height={32} />
            <Box display='flex' flexDirection='column' gap='8px' maxHeight={120} overflow='auto'>
              {[1,2,3].map((i) => (
                <Skeleton key={i} variant="text" width="80%" height={20} />
              ))}
            </Box>
          </Box>
          <Box className={classes.strengthBox} sx={{backgroundColor : '#FFE6E34D'}}>
            <Skeleton variant="text" width={200} height={32} />
            <Box display='flex' flexDirection='column' gap='8px' maxHeight={120} overflow='auto'>
              {[1,2,3].map((i) => (
                <Skeleton key={i} variant="text" width="80%" height={20} />
              ))}
            </Box>
          </Box>
          <Box className={classes.strengthBox}>
            <Skeleton variant="text" width={250} height={32} />
            <Box display="flex" flexDirection="column" gap="20px" maxHeight={400} overflow='auto' mb={5}>
              {[1,2,3].map((i) => (
                <Box key={i}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="rectangular" width="100%" height={48} sx={{ mt: 1 }} />
                </Box>
              ))}
            </Box>
            <Box className={classes.bottomContent}>
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className={classes.container}>
          <Box display='flex' flexDirection='column' gap='8px'>
            <Typography variant='h4-medium' color='neutral.clr-500'>Hey {Session?.user.name}, 👋🏻</Typography>
            <Typography variant='h6-medium' color='neutral.clr-800'>
              Here is your resume review & suggested changes that will help you get shortlisted
            </Typography>
          </Box>
          <Box display='flex' gap='16px' marginTop='12px'>
            <Box className={classes.scoreBox}>
               <CircleProgressbarWithInfo
                  fontVariant="h4-semiBold"
                  size={144}
                  value={feedback?.score}
                  category='score'
                  centerLabel='Relevance score'
                  showComment={false}
                />
                 <Divider sx={{width : '49px' , borderColor : '#CCD4DE'}}/>
                 <Divider orientation='vertical' sx={{height : '75px' , borderColor : '#CCD4DE' , marginBottom : '24px'}}/>
                 <Box className={classes.dividerBox}></Box>
                 <Box display='flex' flexDirection='column' gap='24px'>
                  <Box sx={{ display: 'flex', flexDirection : 'column' , gap:'4px' }}>
                      <LinearProgress
                        variant="determinate"
                        value={feedback?.analysis.skillMatchScore}
                        sx={{ 
                          width: '216px',
                          height: '24px',
                          border : `1px solid #CCD4DE`,
                          borderRadius : theme.spacing( 0 , 1 , 1 , 0),
                          backgroundColor: '#f0f0f0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: skillColor.color,
                          }
                        }}
                      />
                      <Typography variant='body01-bold' color='neutral.clr-700'>
                        Skills match: {feedback?.analysis.skillMatchScore}
                      </Typography>
                    </Box>
                  <Box sx={{ display: 'flex', flexDirection : 'column' , gap:'4px' }}>
                      <LinearProgress
                        variant="determinate"
                        value={feedback?.analysis.relevantExperienceScore}
                        sx={{ 
                          width: '216px',
                          height: '24px',
                          border : `1px solid #CCD4DE`,
                          borderRadius : theme.spacing( 0 , 1 , 1 , 0),
                          backgroundColor: '#f0f0f0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: experienceColor.color,
                          }
                        }}
                      />
                      <Typography variant='body01-bold' color='neutral.clr-700'>
                        Experience match: {feedback?.analysis.relevantExperienceScore}
                      </Typography>
                    </Box>
                 </Box>
                   
            </Box>
            <Box className={classes.summaryBox}>
              <Box display='flex' gap='12px' alignItems='center'>
                <Typography variant='h5-medium' color='neutral.clr-800'>Match Summary</Typography>
                <Typography variant='body01-bold' color={scoreColor.fontColor}sx={{
                backgroundColor : scoreColor.bgColor,
                padding : theme.spacing(0.5,2,0.5,2),
                borderRadius : theme.spacing(1)
              }}>{scoreColor.comment}</Typography>
              </Box>
              <Typography variant='body01-medium' color='neutral.clr-800' sx={{
                maxHeight : '84px',
                overflow : 'auto'
              }}>
                {feedback?.analysis.summary}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.strengthBox} sx={{backgroundColor : '#DCFCE74D'}}>
            <Box display='flex' gap='4px' alignItems='center'>
              <Typography variant='h5-medium' color='neutral.clr-800'>Aligned Strengths</Typography>
              <img 
                src='https://assets.languify.in/images/Stars.png'
                alt='stars'
              />
            </Box>
            <Box display='flex' flexDirection='column' gap='8px' maxHeight={120} overflow='auto'>
            {feedback?.analysis.strengths.map((strength ,index) =>(
                <Typography variant='body01-medium' color='neutral.clr-800'>
                  • &nbsp;{strength}
                </Typography>
              ) )}
            </Box>     
          </Box>
          <Box className={classes.strengthBox} sx={{backgroundColor : '#FFE6E34D'}}>
            <Box display='flex' gap='4px' alignItems='center'>
              <Typography variant='h5-medium' color='neutral.clr-800'>Gaps to Address</Typography>
              <img 
                src='https://assets.languify.in/images/ShockFace.png'
                alt='stars'
              />
            </Box>
            <Box display='flex' flexDirection='column' gap='8px' maxHeight={120} overflow='auto'>
            {feedback?.analysis.gaps.map((gap ,index) =>(
                <Typography variant='body01-medium' color='neutral.clr-800'>
                  • &nbsp;{gap}
                </Typography>
              ) )}
            </Box>     
          </Box>
          <Box className={classes.strengthBox}>
            <Typography variant='h5-medium' color='success.clr-700'>
              Fill the gaps with AI suggested tips
            </Typography>
            <Box display="flex" flexDirection="column" gap="20px" maxHeight={410} overflow='auto' mb={9}>
              {feedback?.analysis.editSuggestions.map((suggestion, index) => {
                const isExpanded = expandedIndexes.includes(index);

                return (
                  <Box key={index} display="flex" flexDirection="column" gap="8px" marginRight={2}>
                    <Box display="flex" flexDirection="column" gap="4px">
                      <Typography
                        variant="body01-medium"
                        sx={
                          !isExpanded
                            ? {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }
                            : {}
                        }
                      >
                        <b>Your content :</b> {suggestion.oldLine}
                      </Typography>

                      {suggestion.oldLine.length > 100 && (
                        <Typography
                          variant='body01-link'
                          onClick={() => handleToggle(index)}
                          color='primary.main'
                          sx={{cursor : 'pointer'}}
                        >
                          {isExpanded ? 'Show less' : 'Read more'}
                        </Typography>
                      )}
                    </Box>
                    <Box className={classes.suggestionBox}>
                      <Box display='flex' gap='4px' alignItems='center'>
                        <Typography variant='body01-bold' color='primary.main'>Suggested replacement:</Typography>
                        <CopyButton
                          content={suggestion.updatedLine}
                          title="copy"
                          iconProps={{ color: "primary" , fontSize : '10px !important' }}
                          size='small'
                        />
                      </Box>
                      <Typography variant='body01-regular'>{suggestion.updatedLine}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
              <Box className={classes.bottomContent}>
                <InfoOutlined
                  sx={{
                    color: '#3C434D',  
                    fontSize: '20px',   
                  }}
                /> &nbsp;
                <Typography variant='body01-semiBold' color='#3C434D'>
                  These suggestions are based on the job requirements. Add them only in your resume if they genuinely apply to you.
                </Typography>
              </Box>
          </Box>
        </Box>
      )}

      </WhiteBox>
      <RateExperienceDialog opendialog={openFeedback} setOpenFeedback={setOpenFeedback} />
    </AppShell>
  );
};

export default ShortlistFeedback;
